import { preventDefault } from '@works-web/settings/modules/browser';
import {
  toEventObject,
  getDateTimeFromTimeString,
} from '@works-web/settings/modules/form/helpers';
import classNames from 'classnames';
import _ from 'lodash';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';

TimeInput.propTypes = {
  name: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
};

const TIME_FORMAT = `HH:mm`;

export function TimeInput({
  name,
  value,
  onChange,
  onFocus = () => {},
  onBlur = () => {},
  disabled = false,
  ...inputProps
}) {
  useEffect(() => {
    const dateTime = getDateTimeFromTimeString(value);
    if (!dateTime.isValid) return;

    lastValidValueRef.current = dateTime.toFormat(TIME_FORMAT);
    if (lastValidValueRef.current === value) return;

    onChange(toEventObject(name, lastValidValueRef.current));
  }, []);

  const [opened, setOpened] = useState(false);
  const handleFocus = (event) => {
    onFocus(event);
  };
  const handleBlur = (event) => {
    onBlur(event);
  };
  const handleClick = () => setOpened(!opened);

  const lastValidValueRef = useRef(value);

  const elemRef = useRef();

  useEffect(() => {
    if (!opened) return;

    const onClickBody = ({ target }) => {
      if (elemRef.current.contains(target)) return;
      setOpened(false);
    };
    document.body.addEventListener('click', onClickBody);
    return () => {
      document.body.removeEventListener('click', onClickBody);
    };
  }, [opened]);

  const handleClickItem = (value) => () => {
    setOpened(false);
    onChange(toEventObject(name, value));
  };

  const listElemRef = useRef();
  const [focusedIndex, setFocusedIndex] = useState(0);
  useEffect(() => {
    if (!opened) return;

    const listElem = listElemRef.current;
    const { top: listElemTop, bottom: listElemBottom } =
      listElem.getBoundingClientRect();
    const itemElems = listElem.querySelectorAll('li');
    const itemsCount = itemElems.length;
    const onKeydown = (event) => {
      const { keyCode } = event;

      switch (keyCode) {
        case 40: {
          // Down
          event.preventDefault();
          return setFocusedIndex((focusedIndex) => {
            const nextIndex = Math.min(focusedIndex + 1, itemsCount - 1);
            const nextElem = listElem.querySelector(
              `li:nth-child(${nextIndex + 1})`,
            );
            const nextElemBottom = nextElem.getBoundingClientRect().bottom;

            if (nextElemBottom > listElemBottom) {
              listElem.scrollTop += nextElemBottom - listElemBottom;
            }

            return nextIndex;
          });
        }
        case 38: {
          // Up
          event.preventDefault();
          return setFocusedIndex((focusedIndex) => {
            const previousIndex = Math.max(focusedIndex - 1, 0);
            const previousElem = listElem.querySelector(
              `li:nth-child(${previousIndex + 1})`,
            );
            const previousElemTop = previousElem.getBoundingClientRect().top;

            if (previousElemTop < listElemTop) {
              listElem.scrollTop += previousElemTop - listElemTop;
            }

            return previousIndex;
          });
        }
        case 13: {
          // Enter
          event.preventDefault();
          const value = listElem
            .querySelector('li.focus')
            .getAttribute('data-value');
          setOpened(false);
          onChange(toEventObject(name, value));
          return;
        }
      }
    };

    const selectedItemElem =
      listElem.querySelector('li.selected') ?? itemElems[0];
    setFocusedIndex([...itemElems].indexOf(selectedItemElem));
    const { top: selectedItemElemTop, bottom: selectedItemElemBottom } =
      selectedItemElem.getBoundingClientRect();
    if (selectedItemElemBottom > listElemBottom) {
      listElem.scrollTop += selectedItemElemBottom - listElemBottom;
    }
    if (selectedItemElemTop < listElemTop) {
      listElem.scrollTop += selectedItemElemTop - listElemTop;
    }
    document.addEventListener('keydown', onKeydown);
    return () => {
      document.removeEventListener('keydown', onKeydown);
    };
  }, [opened]);

  return (
    <div ref={elemRef} className={classNames('time_picker', { on: opened })}>
      <input
        {...inputProps}
        type="text"
        className={classNames({ disabled })}
        name={name}
        value={value}
        onChange={onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={handleClick}
        disabled={disabled}
      />
      {opened && (
        <div className="ly_context">
          <ul ref={listElemRef}>
            {_.range(24).map((hour, index) => {
              const time = `${_.padStart(hour, 2, '0')}:00`;
              return (
                <li
                  key={hour}
                  className={classNames({
                    focus: index === focusedIndex,
                    selected: time === value,
                  })}
                  data-value={time}
                >
                  <a href="#" onClick={preventDefault(handleClickItem(time))}>
                    <span className="text">{time}</span>
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
TimeInput.getNowTimeString = getNowTimeString;

export function getNowTimeString() {
  return DateTime.local().toFormat(TIME_FORMAT);
}
